<template>
  <main>
    <div class="l-main-content__inner">
      <homework-scope-selection
        :is-show="isShowScopeSelection"
        :work="selectedWork"
        v-model:selectedQuestionSetIds="selectedQuestionSetIds"
        @fix="fixScope"
        @cancel="resetScope"
      ></homework-scope-selection>

      <div v-show="!isShowScopeSelection">
        <div class="c-page-header">
          <h1 class="c-page-header-title">宿題作成</h1>
          <!-- 戻る -->
          <div class="c-page-header__return">
            <a
              href="javascript:void(0);"
              class="c-button-white -return -teacher"
              @click="openBackToHomeworkListConfirmModal()"
              >
              <span class="label">戻る</span>
            </a>
          </div>
        </div>
        <form
          class="c-parts-box -white p-homework-form"
          @submit.prevent
        >
          <p class="c-parts-alert-message -large p-homework-form__top-message">生徒画面への反映には時間がかかる場合があります。</p>

          <!-- 学年・学級 -->
          <section v-if="academicYears">
            <fieldset>
              <p class="p-homework-form__title">学年・学級</p>
              <dl class="p-homework-form__list">
                <!-- 学年 -->
                <dt class="p-homework-form__subtitle">学年</dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__text">
                    学年を選択すると、その学年の学級が表示され選択できます。
                    <p class="u-text-alert">※宿題作成後は変更できません。</p>
                  </div>
                  <div
                      v-if= "academicYears.length > 0"
                      id="gradeSelect"
                      class="c-form-radio-area">
                    <label
                      v-for="academicYear in academicYears"
                      :key="academicYear.value"
                      class="c-form-radio"
                      :for="`grade${academicYear.value}`"
                    >
                      <input type="radio"
                        name="grade"
                        :value="academicYear.value"
                        :id="`grade${academicYear.value}`"
                        class="c-form-radio__input"
                        v-model="selectedAcademicYear"
                        @change="fetchSubjects();fetchClassrooms(); displayAfterAcademicYearSelected();"
                      >
                      <span class="c-form-radio__label">{{ academicYear.value }}年生</span>
                    </label>
                  </div>
                  <p
                    v-else
                    class="c-parts-error-message"
                  >選択可能な学年が存在しません。学級の登録を行なってください。</p>
                </dd>
              </dl>
            </fieldset>
            <!-- 学級 -->
            <fieldset id="classSelect" class="u-text-hidden-area">
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">学級</dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__text">学年全体や複数の学級を選択できます。</div>
                  <!-- 全学級チェックボックス -->
                  <label class="c-form-checkbox-area" for="class-all">
                    <input type="checkbox"
                      class="c-form-checkbox"
                      id="class-all"
                      :checked="isAllSelected"
                      @change="toggleAllClassrooms"
                    >
                    <span class="c-form-checkbox-label -large">全学級</span>
                  </label>
                  <!-- 個々の学級チェックボックス -->
                  <div class="p-homework-form__class">
                    <label
                      v-for="classroom in classrooms"
                      :key="classroom.value"
                      class="c-form-checkbox-area class-item"
                      :class="{ 'is-disabled': !classroom.has_students }"
                      :for="`class${classroom.value}`"
                    >
                      <input type="checkbox"
                        name="grade"
                        :value="classroom.value"
                        :id="`class${classroom.value}`"
                        class="c-form-checkbox"
                        v-model="selectedClassrooms"
                        :disabled="!classroom.has_students"
                        @change="selectClassroom"
                      >
                      <span class="c-form-checkbox-label -large">{{ classroom.name }}</span>
                      <!-- NFR-7417: 生徒未登録の学級はホバーで選択不可の理由を表示 -->
                      <span
                        v-if="!classroom.has_students"
                        class="p-homework-form__class-tooltip"
                        role="tooltip"
                      >この学級には生徒が登録されていないため選択できません。</span>
                    </label>
                  </div>
                </dd>
              </dl>
            </fieldset>
          </section>

          <!-- 基本情報 -->
          <section id="basicInfo" class="u-text-hidden-area">
            <fieldset>
              <p class="p-homework-form__title">基本情報</p>
              <!-- 宿題タイトル -->
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">宿題タイトル</dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__text">
                    宿題タイトルは、生徒の画面に表示されます。
                  </div>
                  <div class="c-form-area-half">
                    <div class="c-form-textbox">
                      <input type="text"
                        name="name"
                        id="name"
                        class="c-form-textbox__input"
                        placeholder="例) 生物の観察と分類のしかた"
                        maxlength="30"
                        required
                        v-model="name"
                        autocomplete="off"
                      >
                    </div>
                    <p class="c-form-note">※30文字以内</p>
                  </div>
                </dd>
              </dl>
            </fieldset>
            <!-- 生徒へのメッセージ -->
            <fieldset>
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">生徒へのメッセージ<span class="c-form-optional">任意</span></dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__text">
                    宿題に対して、注意事項などのメッセージを設定できます。宿題メッセージは、生徒の画面に表示されます。
                  </div>
                  <div class="c-form-textarea">
                    <textarea
                      class="c-form-textarea__input"
                      id="message"
                      name="message"
                      placeholder="例）分からないときは教科書を確認して、全問正解を目指そう。"
                      maxlength="100"
                      v-model="message"
                    >
                    </textarea>
                    <p class="c-form-note">※100文字以内（改行可能）</p>
                  </div>
                </dd>
              </dl>
            </fieldset>
          </section>

          <!-- 範囲選択 -->
          <section>
            <!-- 教科選択 -->
            <fieldset id="subjectSelect" class="u-text-hidden-area">
              <p class="p-homework-form__title">範囲選択</p>
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">教科</dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__text">
                    教科を選択すると、教材名が表示されます。
                    <p class="u-text-alert">※宿題作成後は変更できません。</p>
                  </div>
                  <div class="c-form-radio-area" id="subjectSelect">
                    <label
                      v-for="subject in subjects"
                      :key="subject.id"
                      :for="subjectNameValueMap[subject.name]"
                      class="c-form-radio"
                      >
                      <input type="radio"
                        name="subject"
                        :value="subject.id"
                        :id="subjectNameValueMap[subject.name]"
                        class="c-form-radio__input"
                        v-model="selectedSubjectId"
                        @change="fetchWorkIDs"
                      >
                      <span class="c-form-radio__label">{{ subject.name }}</span>
                    </label>
                  </div>
                </dd>
              </dl>
            </fieldset>
            <!-- 教材選択 -->
            <fieldset id="subjectMaterial" class="u-text-hidden-area">
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">教材名</dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__text">
                    <p class="u-text-alert">※宿題作成後は変更できません。</p>
                  </div>
                  <select-single
                    :options="works"
                    v-model:selected="selectedWorkId"
                    :init-blank="false"
                    @change="clearScope"
                  ></select-single>
                </dd>
              </dl>
            </fieldset>
            <!-- 対象問題 -->
            <fieldset id="problemSelect" class="u-text-hidden-area">
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">対象問題</dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__text">
                    「範囲選択」ボタンをクリックし、対象となる問題を選択してください。
                  </div>
                  <div class="p-homework-form__select-area">
                    <!-- FIXME NFR-4166: 別タブで表示する -->
                    <a
                      href="javascript:void(0);"
                      :class="{'c-button-blue -prefix-icon -homework p-homework-form__select-area-button': true, '-disabled': !selectedWorkId}"
                      @click="showScopeSelection"
                    >
                      <span class="label">範囲選択</span>
                    </a>
                    <plain-text-view
                      :markdown-text="selectedScope.title"
                      :prefix="`${selectedScope.prefix}`"
                    ></plain-text-view>
                  </div>
                </dd>
              </dl>
            </fieldset>
          </section>

          <!-- 実施期間 -->
          <section id="periodSelect" class="u-text-hidden-area">
            <fieldset>
              <p class="p-homework-form__title">実施期間</p>
              <p class="p-homework-form__text -independent">生徒が宿題に取り組める期間を設定してください。</p>
              <!-- 開始日時 -->
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">開始日時</dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__date-time">
                    <div class="c-form-textbox">
                      <input type="datetime-local"
                        placeholder=" "
                        value=""
                        class="c-form-textbox__input"
                        :min="minStartDateTime"
                        :max="maxStartDateTime"
                        required
                        autocomplete="off"
                        v-model="startDateTime"
                      >
                    </div>
                  </div>
                </dd>
              </dl>
              <!-- 終了日時 -->
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">終了日時</dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__date-time">
                    <div class="c-form-textbox">
                      <input type="datetime-local"
                        placeholder=" "
                        value=""
                        class="c-form-textbox__input"
                        :min="minEndDateTime"
                        :max="maxEndDateTime"
                        required
                        autocomplete="off"
                        v-model="endDateTime"
                      >
                    </div>
                  </div>
                </dd>
              </dl>
            </fieldset>
          </section>

          <!-- 達成条件・再学習指示 -->
          <section id="otherConditions" class="u-text-hidden-area">
            <fieldset>
              <p class="p-homework-form__title">達成条件・再学習指示</p>
              <!-- 達成条件 -->
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">達成条件</dt>
                <dd class="p-homework-form__contents">
                  <div class="c-form-radio-area">
                    <label
                        v-for="achievementCondition in achievementConditions"
                        :key="achievementCondition.value"
                        class="c-form-radio"
                        :for="`achievement${achievementCondition.value}`"
                    >
                      <input type="radio"
                        name="achievement"
                        :value="achievementCondition.value"
                        :id="`achievement${achievementCondition.value}`"
                        class="c-form-radio__input"
                        required
                        v-model="selectedAchievementCondition"
                      >
                        <span class="c-form-radio__label">{{ achievementCondition.name }}</span>
                    </label>
                  </div>
                </dd>
              </dl>
            </fieldset>
            <!-- 再学習指示 -->
            <fieldset>
              <dl class="p-homework-form__list">
                <dt class="p-homework-form__subtitle">再学習指示<span class="c-form-optional">任意</span></dt>
                <dd class="p-homework-form__contents">
                  <div class="p-homework-form__text">
                    開始日時以前の学習を「宿題の達成」として認めない場合、「宿題の開始日時前に学習済みでも、再度学習させる」をチェックしてください。
                  </div>
                  <label class="c-form-checkbox-area" for="relearning">
                    <input type="checkbox"
                      class="c-form-checkbox"
                      id="relearning"
                      v-model="relearningRequired"
                    >
                    <span class="c-form-checkbox-label -large">宿題の開始日時前に学習済みでも、再度学習させる</span>
                  </label>
                </dd>
              </dl>
            </fieldset>

            <!-- 宿題作成ボタン -->
            <p class="p-homework-form__button">
              <button type="button"
                class="c-button-blue -small-regular -disabled"
                id="homeworkButton"
                autofocus
                @click="addHomework"
              >
                <span class="label">宿題を作成する</span>
              </button>
            </p>
          </section>
        </form>
        <!-- 作成中モーダル -->
        <in-progress-modal
          :is-open="isOpenAddInProgressModal"
          text="作成中"
        >
          ただいま宿題作成中です。作成完了後、宿題一覧に表示されます。<br>
          ※この処理には時間がかかる場合があります。画面を閉じずにこのままお待ちください。
        </in-progress-modal>
        <error-modal></error-modal>
      </div>

      <confirm-red-modal
        :isOpen="isOpenMovePageConfirmModal"
        okLabel="このページから移動する"
        cancelLabel="このページに留まる"
        @ok="movePage"
        @cancel="closeMovePageConfirmModal"
      >
        <template v-slot:text> このページから移動しますか？</template>
        <template v-slot:alertText>移動した場合、宿題は作成されません。</template>
      </confirm-red-modal>

    </div>
  </main>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted } from "vue";
  import { IHomeworkAddResult, IAcademicYearClass, IYearClassClassroom } from "@/consts/interfaces";
  import { SUBJECTS } from "@/consts/subject";
  import { ACHIEVEMENT_CONDITION, INFOTOAST_MESSAGE_PARAMNAME } from "@/consts/homework";
  import { useHomeworkStore } from "@/store/teacher/homework_store";
  import { useLearningProgressStore } from "@/store/teacher/learning_progress_store";
  import { useWorkStore } from "@/store/teacher/work_store";

  const homeworkStore = useHomeworkStore()
  const learningProgressStore = useLearningProgressStore()
  const workStore = useWorkStore()

  // TODO: 必要に応じて interfaces 定義する (NFR-4172)
  const academicSessionId = ref(null);
  const academicYears = ref<Array<any>>(null);
  const selectedAcademicYear = ref(null);
  const academicYearsClassrooms = ref<IAcademicYearClass>(null);
  const classrooms =  ref<Array<IYearClassClassroom>>(null);
  const isAllSelected = ref(false);
  const selectedClassrooms = ref([]);
  const name = ref("");
  const message = ref("");
  const subjects =  ref<Array<any>>(null);
  const selectedSubjectId = ref<number | string>('');
  const works =  ref<Array<any>>([]);
  const selectedWorkId = ref<number | string>('');
  const selectedQuestionSetIds =  ref<Array<number>>([]);
  const startDateTime = ref(null);
  const endDateTime = ref(null);
  const achievementConditions = ACHIEVEMENT_CONDITION
  const selectedAchievementCondition = ref(1);
  const relearningRequired = ref(false);
  const homeworkAddResult = ref<IHomeworkAddResult>(null);
  const isOpenMovePageConfirmModal = ref(false);
  const location = ref("");
  const isShowScopeSelection = ref(false)
  const isOpenAddInProgressModal = ref(false)

  const subjectNameValueMap = computed(() => {
    let newSubjects= {}
    SUBJECTS.forEach(subject => newSubjects[subject.name]=subject.value)
    return newSubjects
  })

  // 選択された教材を返す
  const selectedWork = computed(() => {
    return works.value?.find(work => work.value === selectedWorkId.value) || null
  })

  // 選択された対象問題を返す
  const selectedScope = computed(():{ prefix: string; title: string } => {
    const noScope = { prefix: "", title: "" }

    if (
      !selectedWorkId.value
        || selectedQuestionSetIds.value.length === 0
        || !selectedWork.value
    ) {
      return noScope
    }

    for (const part of selectedWork.value.parts) {
      for (const chapter of part.chapters) {
        for (const section of chapter.sections) {
          for (const questionSet of section.question_sets) {
            if (selectedQuestionSetIds.value.length === 1) {
              if (questionSet.id === selectedQuestionSetIds.value[0]) {
              // 選択された出題単位が1つの場合: {孫項目.sort_order} {選択した出題単位の孫項目名}
              // 例: 「1 身近な生物の観察/生物の特徴と分類」
                return {prefix: section.sort_order, title: section.title}
              }
            } else {
              // 複数の場合: {孫項目.sort_order} {選択した出題単位群の先頭孫項目名} + 他
              // 例: 「1 身近な生物の観察/生物の特徴と分類 + 他」
              if (selectedQuestionSetIds.value.includes(questionSet.id)) {
                return {prefix: section.sort_order, title: `${section.title} + 他`}
              }
            }
          }
        }
      }
    }
    return noScope
  })

  // FIXME: 共通している日付周りの処理はメソッドに切り出す (NFR-4169)
  // カレンダーで選択可能な開始日時の最小値 (現在時以降) を設定する
  const minStartDateTime = computed(() => {
    let today = new Date()
    let year = today.getFullYear()
    let thisMonth = today.getMonth() + 1
    let month = thisMonth.toString().padStart(2, "0")
    let date = today.getDate().toString().padStart(2, "0")
    let hour = today.getHours().toString().padStart(2, "0")
    let minutes = today.getMinutes().toString().padStart(2, "0")
    let newMinStartDateTime = `${year}-${month}-${date}T${hour}:${minutes}`
    return newMinStartDateTime
  })

  // FIXME: 共通している日付周りの処理はメソッドに切り出す (NFR-4169)
  // カレンダーで選択可能な開始日時の最大値 (現在日時〜1年先まで) を設定する
  const maxStartDateTime = computed(() => {
    let today = new Date()
    let year = today.getFullYear() + 1
    let thisMonth = today.getMonth() + 1
    let month = thisMonth.toString().padStart(2, "0")
    let date = today.getDate().toString().padStart(2, "0")
    let hour = today.getHours().toString().padStart(2, "0")
    let minutes = today.getMinutes().toString().padStart(2, "0")
    let newMaxStartDatetime = `${year}-${month}-${date}T${hour}:${minutes}`
    return newMaxStartDatetime
  })

  // FIXME: 共通している日付周りの処理はメソッドに切り出す (NFR-4169)
  // カレンダーで選択可能な終了日時の最小値 (開始日時から1分後) を設定する
  const minEndDateTime = computed(() => {
    let start_date_time = new Date(startDateTime.value)
    start_date_time.setMinutes(start_date_time.getMinutes() + 1)
    let year = start_date_time.getFullYear()
    let this_month = start_date_time.getMonth() + 1
    let month = this_month.toString().padStart(2, "0")
    let date = start_date_time.getDate().toString().padStart(2, "0")
    let hour = start_date_time.getHours().toString().padStart(2, "0")
    let minutes = start_date_time.getMinutes().toString().padStart(2, "0")
    let min_end_datetime = `${year}-${month}-${date}T${hour}:${minutes}`
    return min_end_datetime
  })

  // FIXME: 共通している日付周りの処理はメソッドに切り出す (NFR-4169)
  // カレンダーで選択可能な終了日時の最大値 (終了日時の最小値〜1年先まで) を設定する
  const maxEndDateTime = computed(() => {
    let end_date_time = new Date(minEndDateTime.value)
    let year = end_date_time.getFullYear() + 1
    let this_month = end_date_time.getMonth() + 1
    let month = this_month.toString().padStart(2, "0")
    let date = end_date_time.getDate().toString().padStart(2, "0")
    let hour = end_date_time.getHours().toString().padStart(2, "0")
    let minutes = end_date_time.getMinutes().toString().padStart(2, "0")
    let max_end_date = `${year}-${month}-${date}T${hour}:${minutes}`
    return max_end_date
  })

  onMounted(async() => {
    // 今年度を取得
    academicYearsClassrooms.value = await learningProgressStore.fetchYearClass();
    academicSessionId.value = academicYearsClassrooms.value.defaultAcademicSessionId;

    // 学年一覧を取得
    academicYears.value = academicYearsClassrooms.value.academicYears[academicSessionId.value] || []

    // デフォルトの開始日時・終了日時を設定
    getDefaultStartDateTime()
    getDefaultEndDateTime()

    // 学年選択後にナビゲーションメニューでページ遷移する場合は離脱注意モーダルを表示
    let navs = document.querySelector(".l-navigation__body")
    let navItems = navs.querySelectorAll("a")
    navItems.forEach(function(item) {
      item.addEventListener('click', openMoveToNavMenuPageConfirmModal)
      }
    )
  
    // 学年選択後にサイトロゴを押下した場合は離脱注意モーダルを表示
    let siteLogo = document.querySelector("#site-logo")
    siteLogo.addEventListener('click', openMoveToNavMenuPageConfirmModal)
  
    // 学年選択後にログアウトボタン押下した場合は離脱注意モーダルを表示
    let logout = document.querySelector("#logout")
    logout.addEventListener('click', openMoveToNavMenuPageConfirmModal)
  })


  // 遷移先の設定
  function movePage() {
    window.location.href = location.value
    closeMovePageConfirmModal()
  }

  // 戻るボタン押下時
  function openBackToHomeworkListConfirmModal() {
    const homeworkListUrl = "/teacher/homework/"
    if (selectedAcademicYear.value) {
      location.value = homeworkListUrl
      isOpenMovePageConfirmModal.value= true
    } else {
      window.location.href = homeworkListUrl
    }
  }

  // ナビゲーションメニュー押下時
  function openMoveToNavMenuPageConfirmModal(event) {
    if (selectedAcademicYear.value) {
      event.preventDefault()
      location.value = event.currentTarget.href
      isOpenMovePageConfirmModal.value= true
    }
  }

  function closeMovePageConfirmModal() {
    isOpenMovePageConfirmModal.value= false
  }

  // 学級一覧取得
  function fetchClassrooms(){
    classrooms.value = academicYearsClassrooms.value.classrooms[academicSessionId.value][selectedAcademicYear.value]
    return classrooms.value
  }

  // 教科一覧取得
  async function fetchSubjects() {
    // 選択済みの値をリセットしてから教科一覧を取得する
    selectedClassrooms.value = []
    selectedSubjectId.value = ''
    selectedWorkId.value = ''
    works.value = []
    clearScope()
    isAllSelected.value = false
    await workStore.fetchAvailableWorksBySubject(
      {
        academic_session_id: academicSessionId.value,
        academic_year: selectedAcademicYear.value,
      }
    )
    const newSubjects = workStore.availableWorksBySubject(academicSessionId.value, selectedAcademicYear.value) ?? []
    subjects.value = newSubjects.filter(subject => subject.has_license === true)
    return subjects.value
  }

  function displayAfterAcademicYearSelected() {
    // FIXME: IJ さん JS をそのまま移植しただけなので、必要があれば v-if / v-show に変更してください
    // 学年を選択すると、学級選択エリア、基本情報、範囲選択の教科を表示
    const gradeClass = document.querySelector("#classSelect");
    const basicInfo = document.querySelector("#basicInfo");
    const subjectSelect = document.querySelector("#subjectSelect");
    // 学級選択エリア、基本情報、範囲選択の教科を表示
    gradeClass.classList.add("display");
    basicInfo.classList.add("display");
    subjectSelect.classList.add("display");
  }

  // NFR-7417: 生徒が登録されている（＝選択可能な）学級のみを返す
  function selectableClassrooms(): Array<IYearClassClassroom> {
    return (classrooms.value ?? []).filter((classroom) => classroom.has_students)
  }

  // 「全学級」選択時
  function toggleAllClassrooms() {
    if (isAllSelected.value) {
      selectedClassrooms.value = []
      isAllSelected.value = false
    } else {
      // NFR-7417: 生徒未登録の学級は選択対象に含めない
      selectedClassrooms.value = selectableClassrooms().map((classroom) => classroom.value)
      isAllSelected.value = true
    }
  }

  // 個々の学級選択時
  function selectClassroom() {
    // すべての「選択可能な学級」が選択されたら「全学級」にチェックを入れる
    // そうでない場合は「全学級」のチェックを外す
    // NFR-7417: 生徒未登録の学級は母数から除外する（0件のときは誤ONを防ぐ）
    const selectableCount = selectableClassrooms().length
    if (selectableCount > 0 && selectedClassrooms.value.length === selectableCount) {
      isAllSelected.value = true
    } else {
      isAllSelected.value = false
    }
  }

  // 教科選択されたら教材一覧を取得する
  async function fetchWorkIDs() {
    // 選択済みの値をリセットしてから教材一覧を取得する
    selectedWorkId.value = ''
    clearScope()
    let subject_obj = subjects.value.find(subject => subject.id === selectedSubjectId.value)
    let work_ids = subject_obj.works.map(work => work.id)
    works.value = await homeworkStore.fetchWorks({work_ids: work_ids})
    selectedWorkId.value = works.value[0].value

    // 教材一覧を取得できたら display する
    displayAfterSubjectSelected()
    return works.value
  }

  function displayAfterSubjectSelected() {
    // FIXME: IJ さん JS をそのまま移植しただけなので、必要があれば v-if / v-show に変更してください
    // 教科を選択すると、
    // 宿題出題範囲選択、実施期間、達成条件・再学習指示セクションを表示する
    const subjectMaterial = document.querySelector('#subjectMaterial')
    const problemSelect = document.querySelector('#problemSelect')
    const periodSelect = document.querySelector('#periodSelect')
    const otherConditions = document.querySelector('#otherConditions')
    const homeworkButton = document.querySelector('#homeworkButton')

    subjectMaterial.classList.add('display')
    problemSelect.classList.add('display')
    periodSelect.classList.add('display')
    otherConditions.classList.add('display')
    // FIXME: このボタンの活性タイミングはちょっと考えた方が良さそう
    homeworkButton.classList.remove('-disabled')
  }

  // 宿題範囲選択を表示、宿題作成画面を非表示
  function showScopeSelection() {
    isShowScopeSelection.value = true
  }

  // 宿題範囲選択を非表示、宿題作成画面を表示
  function hideScopeSelection() {
    isShowScopeSelection.value = false
  }

  function clearScope() {
    selectedQuestionSetIds.value = []
  }

  // 宿題範囲選択画面.宿題作成に戻るボタン押下時
  function resetScope() {
    hideScopeSelection()
    clearScope()
  }

  // 宿題範囲選択画面.出題範囲を決定するボタン押下時
  function fixScope() {
    hideScopeSelection()
  }

  // デフォルトの開始日時を設定する
  function getDefaultStartDateTime() {
    let date = new Date()
    // 現在日時の翌日8時をデフォルトの開始日時とする
    date.setDate(date.getDate() + 1)
    date.setHours(8, 0, 0, 0)
    // 2023-06-06T08:00 の形式にする
    let year = date.getFullYear()
    let this_month = date.getMonth() + 1
    let month = this_month.toString().padStart(2, "0")
    let day = date.getDate().toString().padStart(2, "0")
    let hour = date.getHours().toString().padStart(2, "0")
    startDateTime.value = `${year}-${month}-${day}T${hour}:00`
    return startDateTime.value
  }

  // FIXME: 共通している日付周りの処理はメソッドに切り出す (NFR-4169)
  // デフォルトの終了日時を設定する
  function getDefaultEndDateTime() {
    let date = new Date()
    // 現在日時の翌々日8時をデフォルトの終了日時とする
    date.setDate(date.getDate() + 2)
    date.setHours(8, 0, 0, 0)
    // 2023-06-06T08:00 の形式にする
    let year = date.getFullYear()
    let this_month = date.getMonth() + 1
    let month = this_month.toString().padStart(2, "0")
    let day = date.getDate().toString().padStart(2, "0")
    let hour = date.getHours().toString().padStart(2, "0")
    endDateTime.value = `${year}-${month}-${day}T${hour}:00`
    return endDateTime.value
  }

  function moveToHomeworkList(message: string) {
    const currentUrl = window.location.href
    window.location.href = `${currentUrl.replace("homework/add/", "homework/")}?${INFOTOAST_MESSAGE_PARAMNAME}=${message}`
  }

  // 宿題作成ボタン押下時
  async function addHomework() {
    // 必須項目がすべて入力されている場合にPOSTする
    if (!(academicSessionId.value &&
      selectedAcademicYear.value &&
      selectedClassrooms.value.length !== 0 &&
      name.value &&
      startDateTime.value &&
      endDateTime.value &&
      selectedWorkId.value &&
      selectedQuestionSetIds.value.length > 0 &&
      selectedAchievementCondition.value)) {
        return alert("必須項目を入力してください")
      }
    openUploadInProgressModal()
    homeworkAddResult.value = await homeworkStore.fetchHomeworkAdd(
      {
        academic_session_id: academicSessionId.value,
        academic_year: selectedAcademicYear.value,
        classroom_uuids: selectedClassrooms.value,
        name: name.value,
        message: message.value,
        work_id: selectedWorkId.value,
        question_set_ids: selectedQuestionSetIds.value,
        start_datetime: startDateTime.value,
        end_datetime: endDateTime.value,
        achievement_condition: selectedAchievementCondition.value,
        relearning_required: relearningRequired.value
      }
    )
    if (homeworkAddResult.value) {
      moveToHomeworkList(homeworkAddResult.value.message)
    }
    closeUploadInProgressModal()
  }

  function openUploadInProgressModal() {
    isOpenAddInProgressModal.value = true
  }

  function closeUploadInProgressModal() {
    isOpenAddInProgressModal.value = false
  }
</script>

<style scoped>
/* NFR-7417: 生徒未登録の学級を選択不可にし、ホバーで理由をツールチップ表示する */
.class-item.is-disabled {
  position: relative;
  cursor: not-allowed;
}
/* 非活性の学級名・チェックボックスをグレーアウト */
.class-item.is-disabled .c-form-checkbox-label {
  color: #999;
}
.class-item.is-disabled .c-form-checkbox-label::before {
  background: #f0f0f0;
  border-color: #ddd;
}
/* ホバーで表示する理由ツールチップ（黒背景の吹き出し） */
.p-homework-form__class-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  z-index: 10;
  width: max-content;
  max-width: 260px;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 1.2rem;
  line-height: 1.5;
  white-space: normal;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s;
  pointer-events: none;
}
.p-homework-form__class-tooltip::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 16px;
  border: 6px solid transparent;
  border-top-color: rgba(0, 0, 0, 0.8);
}
.class-item.is-disabled:hover .p-homework-form__class-tooltip {
  visibility: visible;
  opacity: 1;
}
</style>
